/**
 * Remembers where a signed-out user was trying to go so the login flow can
 * send them there once they authenticate (ERIN-8413).
 *
 * The destination lives in sessionStorage, not localStorage, on purpose:
 * - handleSignOut() in ApiProviderComponent runs localStorage.clear(), and
 *   SSO users whose session has expired are routed through /logout before
 *   they ever reach the login page, so anything in localStorage is wiped.
 * - sessionStorage is per-tab and survives the Cognito Hosted UI round trip,
 *   which happens in the same tab.
 *
 * Only same-origin relative paths are accepted, and auth-flow pages (login,
 * logout, SAML callbacks, sign-up) are never valid destinations. Entries
 * older than MAX_AGE_MS are ignored so a stale value cannot surprise someone
 * who logs in much later.
 */

export const REDIRECT_STORAGE_KEY = 'pendingRedirectURL';
export const REDIRECT_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

// First path segments that must never be a post-login destination.
const BLOCKED_FIRST_SEGMENTS = new Set([
	'', // "/" (root)
	'login',
	'logout',
	'saml',
	'saml-auth',
	'google',
	'claim',
	'newuser',
	'new-user',
	'signup',
	'new-user-sign-up',
	'new-user-signup',
	'newsamluser',
	'external-user',
	'mobile-redirect',
]);

const getStorage = () => {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
};

/**
 * True when `url` is a relative, same-origin path that is safe to send a
 * freshly authenticated user to.
 */
export const isValidRedirectURL = (url) => {
	if (typeof url !== 'string') return false;
	// Relative paths only. "//host" and "/\host" are protocol-relative and
	// would leave the site.
	if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
		return false;
	}

	if (/[\r\n]/.test(url)) return false;

	const pathname = url.split(/[?#]/)[0];
	const segments = pathname.split('/').filter(Boolean);
	const first = segments[0] ?? '';
	if (BLOCKED_FIRST_SEGMENTS.has(first)) return false;
	// "/:companycode/login" and friends.
	if (segments.at(-1) === 'login') return false;

	return true;
};

export const clearRedirectURL = () => {
	try {
		getStorage()?.removeItem(REDIRECT_STORAGE_KEY);
	} catch {
		// Storage unavailable (private mode, disabled). Nothing to clear.
	}
};

const readStoredURL = () => {
	try {
		const raw = getStorage()?.getItem(REDIRECT_STORAGE_KEY);
		if (!raw) return null;

		const { url, ts } = JSON.parse(raw);
		const expired =
			typeof ts !== 'number' || Date.now() - ts > REDIRECT_MAX_AGE_MS;
		if (expired || !isValidRedirectURL(url)) {
			clearRedirectURL();
			return null;
		}

		return url;
	} catch {
		clearRedirectURL();
		return null;
	}
};

/**
 * Store `url` as the post-login destination. Returns false (and stores
 * nothing) when the url is not a valid destination, so callers can pass the
 * current location blindly without clobbering a good value with "/login".
 */
export const rememberRedirectURL = (url) => {
	if (!isValidRedirectURL(url)) return false;
	try {
		getStorage()?.setItem(
			REDIRECT_STORAGE_KEY,
			JSON.stringify({ url, ts: Date.now() })
		);
		return true;
	} catch {
		return false;
	}
};

/** Store the page the browser is on right now (path + query + hash). */
export const rememberCurrentLocation = () => {
	const { pathname, search, hash } = window.location;
	return rememberRedirectURL(`${pathname}${search}${hash}`);
};

/** Read the stored destination without clearing it. */
export const peekRedirectURL = (fallback = null) => readStoredURL() ?? fallback;

/** Read the stored destination and clear it. */
export const consumeRedirectURL = (fallback = '/dashboard') => {
	const url = readStoredURL();
	clearRedirectURL();
	return url ?? fallback;
};
